/**
 * @module features/users/components/UserStatsCards
 * @description KPI metric cards modeled after modern dashboard architecture (Elera reference).
 * Features rounded-2xl cards, dark squircle icon tiles, Inter bold typography, and semantic status pills.
 */

import * as React from "react";
import { Users, ShieldCheck, UserCheck, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { UserStats } from "@/types/users";

interface UserStatsCardsProps {
  stats?: UserStats;
  isLoading?: boolean;
}

export function UserStatsCards({ stats, isLoading }: UserStatsCardsProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3.5 sm:p-4 rounded-2xl border border-border/60 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-12" />
              </div>
            </div>
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: "Total Accounts",
      value: stats.total_users,
      icon: Users,
      badgeText: "+14%",
      badgeStyle:
        "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/50",
    },
    {
      label: "Super Admins",
      value: stats.total_super_admins,
      icon: ShieldCheck,
      badgeText: "Root Access",
      badgeStyle:
        "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/50",
    },
    {
      label: "Client Portals",
      value: stats.total_clients,
      icon: UserCheck,
      badgeText: "Active",
      badgeStyle:
        "text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-850 border border-zinc-200 dark:border-zinc-800",
    },
    {
      label: "2FA Adoption",
      value: `${stats.two_factor_adoption_rate}%`,
      icon: Activity,
      badgeText: "Protected",
      badgeStyle:
        "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/50",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <div
            key={idx}
            className="flex items-center justify-between p-3.5 sm:p-4 rounded-2xl border border-border/70 dark:border-zinc-800/80 bg-card dark:bg-zinc-950 shadow-xs hover:shadow-sm transition-all duration-150"
          >
            <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
              {/* Dark squircle icon tile from reference design */}
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 shadow-xs shrink-0 ring-1 ring-black/5 dark:ring-white/10">
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
              </div>

              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs font-medium text-muted-foreground truncate">
                  {card.label}
                </p>
                <p className="text-lg sm:text-2xl font-bold tracking-tight text-foreground truncate">
                  {card.value}
                </p>
              </div>
            </div>

            {/* Semantic trend/status pill */}
            <span
              className={`hidden sm:inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${card.badgeStyle}`}
            >
              {card.badgeText}
            </span>
          </div>
        );
      })}
    </div>
  );
}
